import React, { useState } from 'react';
import { Copy, Check, RefreshCw, ThumbsUp, ThumbsDown } from 'lucide-react';

interface MessageActionsProps {
  text: string;
  onRegenerate?: () => void;
  showRegenerate?: boolean;
  onFeedback?: (type: 'positive' | 'negative') => void;
}

export function MessageActions({
  text,
  onRegenerate,
  showRegenerate = false,
  onFeedback,
}: MessageActionsProps) {
  const [copied, setCopied] = useState(false);
  const [feedback, setFeedback] = useState<'positive' | 'negative' | null>(null);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API not available
    }
  };

  const handleFeedback = (type: 'positive' | 'negative') => {
    setFeedback(type);
    onFeedback?.(type);
  };

  const buttonBaseClass = `
    p-2 md:p-1.5 rounded-lg
    text-gray-400 hover:text-gray-600 dark:hover:text-gray-200
    hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-600
    transition-all duration-150
    touch-manipulation
    focus:outline-none focus:ring-2 focus:ring-sky-200
  `;

  return (
    <div className="flex items-center gap-0.5 mt-2 bg-gray-50/80 dark:bg-gray-800/80 rounded-lg p-0.5 border border-gray-100 dark:border-gray-700">
      {/* Copy button */}
      <button
        onClick={handleCopy}
        className={`${buttonBaseClass} ${copied ? 'text-green-500' : ''}`}
        title={copied ? 'Copied!' : 'Copy to clipboard'}
        aria-label={copied ? 'Copied to clipboard' : 'Copy to clipboard'}
      >
        {copied ? (
          <Check className="w-4 h-4" />
        ) : (
          <Copy className="w-4 h-4" />
        )}
      </button>

      {/* Regenerate button */}
      {showRegenerate && onRegenerate && (
        <button
          onClick={onRegenerate}
          className={buttonBaseClass}
          title="Regenerate response"
          aria-label="Regenerate response"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      )}

      {/* Divider */}
      <div className="w-px h-4 bg-gray-200 dark:bg-gray-600 mx-0.5" />

      {/* Feedback buttons */}
      <button
        onClick={() => handleFeedback('positive')}
        className={`${buttonBaseClass} ${feedback === 'positive' ? 'text-green-500 bg-green-50 dark:bg-green-900/20' : ''}`}
        title="Good response"
        aria-label="Mark as good response"
        aria-pressed={feedback === 'positive'}
      >
        <ThumbsUp className="w-4 h-4" />
      </button>

      <button
        onClick={() => handleFeedback('negative')}
        className={`${buttonBaseClass} ${feedback === 'negative' ? 'text-red-500 bg-red-50 dark:bg-red-900/20' : ''}`}
        title="Poor response"
        aria-label="Mark as poor response"
        aria-pressed={feedback === 'negative'}
      >
        <ThumbsDown className="w-4 h-4" />
      </button>
    </div>
  );
}
